import { authService } from '../../services/authService';

/**
 * WebSocket Service
 * Gerencia conexões WebSocket com o backend
 */

export interface WebSocketMessage {
  type: 'detection' | 'alert' | 'camera_status' | 'error';
  data: any;
  timestamp: string;
}

export interface DetectionMessage {
  camera_id: string;
  camera_name: string;
  person_count: number;
  classifications: {
    male: number;
    female: number;
    young: number;
    adult: number;
    elderly: number;
  };
  confidence: number;
  timestamp: string;
  image_url?: string;
}

export interface AlertMessage {
  id: string;
  type: 'high_density' | 'rapid_increase' | 'unusual_time' | 'demographic_shift' | 'camera_offline';
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  message: string;
  camera_id?: string;
  camera_name?: string;
  timestamp: string;
  metadata?: Record<string, any>;
}

export interface CameraStatusMessage {
  camera_id: string;
  camera_name: string;
  status: 'online' | 'offline' | 'error';
  last_detection?: string;
}

export type MessageCallback = (message: WebSocketMessage) => void;

export class WebSocketService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 3000;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private callbacks: MessageCallback[] = [];
  private url: string;
  private shouldReconnect = true;

  constructor(url: string) {
    this.url = url;
  }

  /**
   * Conectar ao WebSocket
   */
  connect(): void {
    if (authService.getUser()?.role === 'partner') return;
    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }
    
    try {
      const connectionUrl = new URL(this.url, window.location.href);
      const token = authService.getToken();
      if (token) connectionUrl.searchParams.set('token', token);
      this.ws = new WebSocket(connectionUrl.toString());

      this.ws.onopen = this.handleOpen.bind(this);
      this.ws.onmessage = this.handleMessage.bind(this);
      this.ws.onerror = this.handleError.bind(this);
      this.ws.onclose = this.handleClose.bind(this);
    } catch (error) {
      console.error('[WebSocket] Erro ao conectar:', error);
      this.scheduleReconnect();
    }
  }

  /**
   * Desconectar do WebSocket
   */
  disconnect(): void {
    this.shouldReconnect = false;
    
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  /**
   * Adicionar callback para mensagens
   */
  addCallback(callback: MessageCallback): () => void {
    this.callbacks.push(callback);
    
    // Retornar função para remover o callback
    return () => {
      this.callbacks = this.callbacks.filter(cb => cb !== callback);
    };
  }

  /**
   * Verificar se está conectado
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Handler de conexão aberta
   */
  private handleOpen(): void {
    this.reconnectAttempts = 0;
    this.notifyCallbacks({
      type: 'camera_status',
      data: { status: 'connected' },
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Handler de mensagem recebida
   */
  private handleMessage(event: MessageEvent): void {
    try {
      const message: WebSocketMessage = JSON.parse(event.data);
      this.notifyCallbacks(message);
    } catch (error) {
      console.error('[WebSocket] Erro ao processar mensagem:', error);
    }
  }

  /**
   * Handler de erro
   */
  private handleError(event: Event): void {
    console.error('[WebSocket] ❌ Erro:', event);
    this.notifyCallbacks({
      type: 'error',
      data: { message: 'Erro na conexão WebSocket' },
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Handler de conexão fechada
   */
  private handleClose(event: CloseEvent): void {
    console.log('[WebSocket] 🔌 Conexão fechada:', event.code, event.reason);
    this.ws = null;

    if (this.shouldReconnect) {
      this.scheduleReconnect();
    }
  }

  /**
   * Agendar reconexão
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[WebSocket] Máximo de tentativas de reconexão atingido');
      this.notifyCallbacks({
        type: 'error',
        data: { message: 'Não foi possível reconectar ao servidor' },
        timestamp: new Date().toISOString(),
      });
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * this.reconnectAttempts;
    
    console.log(
      `[WebSocket] 🔄 Tentando reconectar em ${delay}ms (${this.reconnectAttempts}/${this.maxReconnectAttempts})`
    );

    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, delay);
  }

  /**
   * Notificar todos os callbacks
   */
  private notifyCallbacks(message: WebSocketMessage): void {
    this.callbacks.forEach(callback => {
      try {
        callback(message);
      } catch (error) {
        console.error('[WebSocket] Erro no callback:', error);
      }
    });
  }
}

/**
 * Singleton para gerenciar instâncias de WebSocket
 */
class WebSocketManager {
  private services: Map<string, WebSocketService> = new Map();

  getService(url: string): WebSocketService {
    if (!this.services.has(url)) {
      this.services.set(url, new WebSocketService(url));
    }
    return this.services.get(url)!;
  }

  disconnect(url: string): void {
    const service = this.services.get(url);
    if (service) {
      service.disconnect();
      this.services.delete(url);
    }
  }

  disconnectAll(): void {
    this.services.forEach(service => service.disconnect());
    this.services.clear();
  }
}

export const webSocketManager = new WebSocketManager();
